'use client';

import cx from 'classnames';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { Attributes, Children, PropsWithChildren, ReactElement, ReactNode, cloneElement, isValidElement } from 'react';
import { useFormContext } from 'react-hook-form';

import styles from './formItem.module.scss';

interface Props {
  id?: string;
  label?: ReactNode;
  name?: string;
  error?: string | ReactNode;
  description?: string | ReactNode[];
  withoutExplain?: boolean;
  className?: string;
  children: ReactElement;
  inheritLabel?: boolean;
}

const extractTextNodes = (node: ReactNode): string => {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return node.toString();
  if (isValidElement(node)) return extractTextNodes((node.props as PropsWithChildren).children);
  if (Array.isArray(node)) {
    return Children.toArray(node)
      .map((child) => extractTextNodes(child))
      .join('');
  }
  return '';
};

export const FormItem = ({
  id,
  label,
  name,
  error,
  description,
  withoutExplain,
  inheritLabel,
  className,
  children,
}: Props) => {
  const { register } = useFormContext() ?? {};
  const isShowExplain = !withoutExplain && !!(error || description);
  const key = typeof error === 'string' ? error : extractTextNodes(error) || 'description';

  return (
    <div className={cx(styles.wrapper, className)}>
      {label && (
        <div className={cx(styles.labelWrapper, { [styles.inheritLabel]: inheritLabel })}>
          <label htmlFor={id}>{label}</label>
        </div>
      )}
      <div className={styles.controlWrapper}>
        {cloneElement(children, {
          id,
          isError: error !== undefined ? !!error : undefined,
          ...(name ? register?.(name) : undefined),
        } as Attributes)}
        <AnimatePresence mode='popLayout' initial={!description}>
          {isShowExplain && (
            <m.div
              key={key}
              initial={{ y: -2, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 4, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={cx(styles.explain, { [styles.isError]: !!error })}
            >
              <div className={styles.message}>{error || description}</div>
            </m.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
